/**
 * NOLAS 2026, (c) Mikaël FOURNY. Fork de la plateforme TNE 2026.
 * src/pwa/chargementModule.ts
 *
 * Rôle :
 *   Rattraper le cas « un morceau de l'application n'existe plus ». Chaque
 *   écran est chargé à la demande : quand une nouvelle version est mise en
 *   ligne, les fichiers de l'ancienne disparaissent, et l'onglet resté
 *   ouvert demande un fichier qui n'est plus là. Sans filet, l'écran reste
 *   BLANC et plus aucune navigation ne répond.
 *
 * Pourquoi, et comment on l'a su :
 *   Signalé par Mika le 2026-09-27 : après avoir imprimé son badge, page
 *   blanche sur les deux onglets, et le retour en arrière blanc aussi.
 *   Mesuré : le serveur renvoyait `index.html` (HTTP 200, text/html) pour un
 *   fichier JavaScript disparu, le navigateur tentait d'exécuter du HTML, le
 *   module ne se chargeait jamais. Le serveur répond désormais 404
 *   (public/.htaccess), et ce garde-fou transforme ce 404 en rechargement.
 *
 * Ce qu'il ne fait pas :
 *   il ne recharge JAMAIS deux fois de suite. Si la version fraîche échoue
 *   elle aussi, on n'entre pas dans une boucle de rechargement : c'est le
 *   défaut le plus pénible qui soit, la page clignote et personne ne peut
 *   rien lire, pas même un message d'erreur.
 *
 * Mikael - chef de projet - 2026-09-27
 */

/** Marqueur de session : un seul rechargement automatique par onglet. */
const CLE_RECHARGE = 'app.module.recharge';

/**
 * L'erreur ressemble-t-elle à un morceau d'application manquant ?
 *
 * Les navigateurs ne s'accordent pas sur le message : Chrome dit
 * « Failed to fetch dynamically imported module », Safari « Importing a
 * module script failed », Firefox « error loading dynamically imported
 * module ». On reconnaît aussi l'exécution de HTML servi comme du
 * JavaScript, symptôme exact du défaut mesuré.
 */
export function estModuleManquant(erreur: unknown): boolean {
  const message =
    erreur instanceof Error
      ? `${erreur.name} ${erreur.message}`
      : typeof erreur === 'string'
        ? erreur
        : '';
  if (message === '') return false;
  const m = message.toLowerCase();
  return (
    m.includes('dynamically imported module') ||
    m.includes('importing a module script failed') ||
    m.includes('error loading dynamically imported') ||
    m.includes('failed to fetch dynamically') ||
    // HTML servi à la place du JavaScript : le moteur bute sur « < ».
    m.includes("unexpected token '<'") ||
    m.includes('unexpected token <')
  );
}

/**
 * Recharge une fois, depuis le serveur, si l'erreur est bien celle-là.
 * Renvoie true si un rechargement a été déclenché.
 */
export function rechargerSiModuleManquant(erreur: unknown): boolean {
  if (!estModuleManquant(erreur)) return false;
  try {
    if (window.sessionStorage.getItem(CLE_RECHARGE) === '1') {
      // Déjà tenté dans cet onglet : on laisse l'erreur remonter plutôt que
      // de faire clignoter la page indéfiniment.
      return false;
    }
    window.sessionStorage.setItem(CLE_RECHARGE, '1');
  } catch {
    // Stockage indisponible (navigation privée verrouillée) : on recharge
    // quand même, une fois. Le pire reste l'écran blanc.
  }
  window.location.reload();
  return true;
}

/**
 * Branche le garde-fou sur les deux voies par lesquelles cette erreur
 * remonte : une promesse rejetée sans `catch` (import dynamique de React) et
 * l'erreur globale.
 */
export function surveillerLeChargementDesModules(): () => void {
  const surRejet = (e: PromiseRejectionEvent): void => {
    if (rechargerSiModuleManquant(e.reason)) {
      e.preventDefault();
    }
  };
  const surErreur = (e: ErrorEvent): void => {
    rechargerSiModuleManquant(e.error ?? e.message);
  };
  window.addEventListener('unhandledrejection', surRejet);
  window.addEventListener('error', surErreur);
  return () => {
    window.removeEventListener('unhandledrejection', surRejet);
    window.removeEventListener('error', surErreur);
  };
}
